import { ExternalLink, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PortalBadge } from "@/components/PortalBadge";
import { usePortalListings, useExcludeListing, useIncludeListing } from "@/api/portalListings";
import type { PortalListing } from "@/api/portalListings";

function fmtPreis(p: string | null, currency: string | null): string {
  if (p === null) return "—";
  const n = Number(p);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("de-DE", { style: "currency", currency: currency ?? "EUR" });
}

function fmtVersand(l: PortalListing): string {
  if (l.shippingFree === null) return "—";
  if (l.shippingFree) return "inkl. Versand";
  const c = l.shippingCost === null ? null : Number(l.shippingCost);
  return c !== null && Number.isFinite(c)
    ? `zzgl. ${c.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}`
    : "zzgl. Versand";
}

function fmtDatum(iso: string | null): string {
  // Explizite 2-digit-Optionen: 'de-DE' ohne Options liefert bei einstelligen
  // Monaten kein fuehrendes Null (z.B. "15.7.2026" statt "15.07.2026").
  return iso
    ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "—";
}

function statusRowAccent(status: PortalListing["status"]): string {
  if (status === "inactive") return "border-l-2 border-l-coral";
  if (status === "active") return "border-l-2 border-l-success/50";
  return "border-l-2 border-l-line";
}

/** Gruppierung fuer Mengen-Varianten derselben Auktion (User-Wunsch 2026-07-18, Optik v2 2026-07-20).
 *  Gleiche Auktion = gleiche URL im selben Portal-Konto: eBay-Varianten-Listings
 *  (1/2/10/15 Stueck in EINER Auktion) teilen sich die itm-URL — es gibt keine
 *  separate Listing-ID im API-Response, und die URL IST der "Link zur gemeinsamen
 *  Auktion", um den es geht. Einzel-Angebote bleiben ungruppiert.
 *
 *  Darstellung: Varianten stehen als BLOCK untereinander, mit eigener Kopfzeile
 *  ("Gemeinsame Auktion — n Mengen-Varianten" + Link) und getoenten Zeilen —
 *  der kleine Chip der ersten Fassung war laut User-Feedback nicht auffaellig genug. */
const auctionKey = (l: PortalListing) => `${l.portal}|${l.accountKey}|${l.url}`;

type RowBlock =
  | { kind: "single"; row: PortalListing }
  | { kind: "auction"; nr: number; url: string; portal: string; accountKey: string; rows: PortalListing[] };

function buildRowBlocks(rows: PortalListing[]): RowBlock[] {
  const counts = new Map<string, number>();
  for (const l of rows) counts.set(auctionKey(l), (counts.get(auctionKey(l)) ?? 0) + 1);

  const blocks: RowBlock[] = [];
  const openGroups = new Map<string, Extract<RowBlock, { kind: "auction" }>>();
  let nr = 0;
  for (const l of rows) {
    const k = auctionKey(l);
    if ((counts.get(k) ?? 0) > 1) {
      // Varianten derselben Auktion werden an der Position der ERSTEN Variante
      // zusammengezogen — die Backend-Sortierung "rot zuerst" bleibt dadurch auf
      // Block-Ebene erhalten (ein Block mit roter Variante steht weiterhin oben).
      let g = openGroups.get(k);
      if (!g) {
        g = { kind: "auction", nr: ++nr, url: l.url, portal: l.portal, accountKey: l.accountKey, rows: [] };
        openGroups.set(k, g);
        blocks.push(g);
      }
      g.rows.push(l);
    } else {
      blocks.push({ kind: "single", row: l });
    }
  }
  return blocks;
}

/** Verkaufs-Angebote (aktive Portale) fuer den Beschreibung-Reiter eines Grundartikels.
 *  "Rot zuerst": das Backend (listListingsForGrundartikel) sortiert weggefallene
 *  Angebote nach oben — die linke Akzentlinie hier spiegelt genau dieses Signal,
 *  keine zusaetzliche Deko. */
export default function VerkaufslinksTab({ sku }: { sku: string }) {
  const { data, isLoading, error } = usePortalListings(sku);
  const exclude = useExcludeListing(sku);
  const include = useIncludeListing(sku);

  if (isLoading) {
    return <div className="text-sm text-muted">Verkaufs-Angebote werden geladen…</div>;
  }

  if (error) {
    return (
      <div className="text-sm text-coral">
        Verkaufs-Angebote konnten nicht geladen werden. Die Liste ist deshalb leer — das heisst{" "}
        <strong>nicht</strong>, dass keine Angebote existieren.
      </div>
    );
  }

  const rows = data ?? [];
  if (rows.length === 0) {
    return <div className="text-sm text-muted">Keine aktiven Verkaufs-Angebote gefunden.</div>;
  }

  const blocks = buildRowBlocks(rows);
  const auctionCount = blocks.filter((b) => b.kind === "auction").length;

  const renderListingRow = (l: PortalListing, inGroup: boolean) => (
    <tr
      key={l.id}
      className={`${statusRowAccent(l.status)} ${inGroup ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-panel-2/40"}`}
    >
      <td className="py-2 pl-2 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <PortalBadge portal={l.portal} />
          <span className="font-mono text-xs text-muted">{l.accountKey}</span>
        </div>
      </td>
      {/* Overflow-Fix (User-Fund 2026-07-18): lange Amazon-Titel malten ueber die
          Stueck-/Preis-Spalten. Ursache: `truncate` auf einem Flex-Kind ohne
          `min-w-0` greift nicht (Flex-Items schrumpfen nicht unter ihre
          Content-Breite), und `max-w-xs` auf dem <td> wird vom Auto-Table-Layout
          nicht zuverlaessig respektiert. Fix: Breite auf dem block-level <a>
          begrenzen + `min-w-0` auf dem truncateten Span; voller Titel via title-Attr. */}
      <td className="py-2 pr-4">
        {/* Gruppen-Varianten bekommen eine durchgehende Verbindungslinie + Einrueckung —
            die sichtbare "Klammer", dass alles EINE Auktion ist. */}
        <div className={inGroup ? "border-l-[3px] border-primary/50 pl-3" : ""}>
          <a
            href={l.url}
            target="_blank"
            rel="noopener noreferrer"
            title={l.title ?? l.url}
            className="flex max-w-md items-center gap-1 text-primary underline underline-offset-2 hover:text-primary-ink"
          >
            <span className="min-w-0 truncate">{l.title ?? l.url}</span>
            <ExternalLink className="size-3 shrink-0" aria-hidden />
          </a>
          <div className="font-mono text-xs text-muted-2">{l.soldItemSku}</div>
        </div>
      </td>
      <td className="py-2 text-right font-mono tabular-nums font-medium">{l.stueckzahl}</td>
      <td className="py-2 text-right font-mono tabular-nums">{fmtPreis(l.price, l.currency)}</td>
      <td className="py-2 whitespace-nowrap text-ink-2">{fmtVersand(l)}</td>
      <td className="py-2 whitespace-nowrap">
        {l.status === "active" && (
          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-success/20 text-success">
            Aktiv
          </span>
        )}
        {l.status === "inactive" && (
          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-coral-soft text-coral">
            Nicht mehr aktiv — seit {fmtDatum(l.inactiveSince)}
          </span>
        )}
        {l.status === "excluded" && (
          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-panel-2 text-muted">
            Ausgeschlossen
          </span>
        )}
      </td>
      <td className="py-2 text-right">
        {l.status === "inactive" && (
          <Button
            variant="ghost"
            size="sm"
            disabled={exclude.isPending}
            onClick={() => exclude.mutate(l.id)}
          >
            Ausschliessen
          </Button>
        )}
        {l.status === "excluded" && (
          <Button
            variant="ghost"
            size="sm"
            disabled={include.isPending}
            onClick={() => include.mutate(l.id)}
          >
            Wieder aufnehmen
          </Button>
        )}
      </td>
    </tr>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-muted text-left">
            <th className="pb-2">Portal / Konto</th>
            <th className="pb-2">Angebot</th>
            <th className="pb-2 text-right">Stück</th>
            <th className="pb-2 text-right">Preis</th>
            <th className="pb-2">Versand</th>
            <th className="pb-2">Status</th>
            <th className="pb-2 w-32" />
          </tr>
        </thead>
        <tbody className="divide-y divide-line-2">
          {blocks.map((b) => {
            if (b.kind === "single") return renderListingRow(b.row, false);
            return [
              <tr key={`auction-${b.nr}`} className="bg-primary/10">
                <td colSpan={7} className="py-2.5 pl-2 pr-4">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink">
                      <Link2 className="size-4 text-primary" aria-hidden />
                      Gemeinsame Auktion{auctionCount > 1 ? ` ${b.nr}` : ""} — {b.rows.length}{" "}
                      Mengen-Varianten in einer Auktion
                    </span>
                    <PortalBadge portal={b.portal} />
                    <span className="font-mono text-xs text-muted">{b.accountKey}</span>
                    <a
                      href={b.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary underline underline-offset-2 hover:text-primary-ink"
                    >
                      Auktion öffnen
                      <ExternalLink className="size-3.5" aria-hidden />
                    </a>
                  </div>
                </td>
              </tr>,
              ...b.rows.map((l) => renderListingRow(l, true)),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}
